import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Calendar, MapPin, User, Image as ImageIcon } from 'lucide-react';
import { adminEventService } from '../../services/eventService';
import { LoadingSpinner, Modal, Button } from '../ui';
import { toast } from '../ui/Toaster';
import type { Event } from '../../types';

interface PendingEventsPanelProps {
  onEventProcessed?: () => void;
}

interface RejectModalState {
  isOpen: boolean;
  event: Event | null;
  reason: string;
  submitting: boolean;
}

const PendingEventsPanel: React.FC<PendingEventsPanelProps> = ({ onEventProcessed }) => {
  const [pendingEvents, setPendingEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [rejectModal, setRejectModal] = useState<RejectModalState>({
    isOpen: false,
    event: null,
    reason: '',
    submitting: false,
  });

  useEffect(() => {
    loadPendingEvents();
  }, []);

  const loadPendingEvents = async () => {
    try {
      setIsLoading(true);
      const events = await adminEventService.getPendingEvents();
      setPendingEvents(events);
    } catch (error: any) {
      console.error('Failed to load pending events:', error);
      toast.error('Fout', 'Kon evenementen niet laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (eventId: number) => {
    try {
      setProcessingId(eventId);
      await adminEventService.approveEvent(eventId);
      toast.success('Gelukt', 'Evenement gemarkeerd als gecontroleerd');
      setPendingEvents(prev => prev.filter(event => event.id !== eventId));
      onEventProcessed?.();
    } catch (error: any) {
      console.error('Failed to approve event:', error);
      toast.error('Fout', error.message || 'Kon evenement niet markeren als gecontroleerd');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (event: Event) => {
    setRejectModal({ isOpen: true, event, reason: '', submitting: false });
  };

  const closeRejectModal = () => {
    if (rejectModal.submitting) return;
    setRejectModal({ isOpen: false, event: null, reason: '', submitting: false });
  };

  const confirmReject = async () => {
    const event = rejectModal.event;
    if (!event) return;

    try {
      setRejectModal(prev => ({ ...prev, submitting: true }));
      setProcessingId(event.id);

      await adminEventService.rejectEvent(event.id, rejectModal.reason.trim() || undefined);
      toast.success('Gelukt', 'Evenement gedepubliceerd');
      setPendingEvents(prev => prev.filter(e => e.id !== event.id));
      onEventProcessed?.();
      setRejectModal({ isOpen: false, event: null, reason: '', submitting: false });
    } catch (error: any) {
      console.error('Failed to reject event:', error);
      toast.error('Fout', error.message || 'Kon evenement niet depubliceren');
      setRejectModal(prev => ({ ...prev, submitting: false }));
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: Event['status']) => {
    if (status === 'PUBLISHED') {
      return <span className="sf-badge-lime">Live</span>;
    }
    return <span className="sf-badge-cyan">In behandeling</span>;
  };

  if (isLoading) {
    return (
      <div className="sf-card p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" text="Evenementen laden..." />
        </div>
      </div>
    );
  }

  return (
    <div className="sf-card">
      <div className="px-6 py-4 border-b border-white/[.08]">
        <div className="flex items-center space-x-2">
          <Clock className="h-5 w-5 text-neon-cyan" />
          <h2 className="text-lg font-bold text-fg-bright">
            Te controleren ({pendingEvents.length})
          </h2>
        </div>
        <p className="text-sm text-fg-muted mt-1">
          Evenementen die wachten op controle door een beheerder
        </p>
      </div>

      <div className="p-4 space-y-3">
        {pendingEvents.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-neon-lime mb-4" />
            <h3 className="text-lg font-semibold text-fg-bright mb-2">
              Helemaal bijgewerkt!
            </h3>
            <p className="text-fg-muted">
              Er zijn geen evenementen die nog gecontroleerd moeten worden.
            </p>
          </div>
        ) : (
          pendingEvents.map((event) => (
            <div key={event.id} className="sf-item p-4">
              <div className="flex items-center space-x-4">
                {/* Image Thumbnail */}
                <div className="flex-shrink-0">
                  {event.image ? (
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-16 h-16 rounded-item object-cover border border-white/[.08]"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-item bg-white/[.05] border border-white/[.08] flex items-center justify-center">
                      <ImageIcon className="h-6 w-6 text-fg-faint" />
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-fg-bright truncate">
                        {event.title}
                      </h3>

                      <div className="flex items-center space-x-4 mt-1 text-sm text-fg-muted">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1 text-neon-cyan" />
                          <span>
                            {formatDate(event.startDate)}
                            {event.endDate && event.endDate !== event.startDate &&
                              ` - ${formatDate(event.endDate)}`
                            }
                          </span>
                        </div>

                        <div className="flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-neon-pink" />
                          <span className="truncate">{event.location}</span>
                        </div>

                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1 text-fg-faint" />
                          <span>{event.creator?.username || 'Onbekend'}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mt-2">
                        {getStatusBadge(event.status)}

                        <span className="inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body">
                          {event.type}
                        </span>

                        {event.isPublic === false && (
                          <span className="sf-badge-pink">
                            Privé
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => openRejectModal(event)}
                        disabled={processingId === event.id}
                        className="sf-btn-secondary p-2.5"
                        title="Depubliceren"
                      >
                        <XCircle className="h-4 w-4 text-neon-pink-soft" />
                      </button>

                      <button
                        onClick={() => handleApprove(event.id)}
                        disabled={processingId === event.id}
                        className="sf-btn-accept p-2.5"
                        title="Markeer als gecontroleerd"
                      >
                        {processingId === event.id && !rejectModal.isOpen ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Depubliceren: reden opgeven (geen window.prompt) */}
      <Modal isOpen={rejectModal.isOpen} onClose={closeRejectModal} title="Evenement depubliceren" size="sm">
        <p className="text-sm text-fg-body mb-4">
          Je staat op het punt om <strong className="text-fg-bright">&quot;{rejectModal.event?.title}&quot;</strong> offline
          te halen. De maker ziet deze reden en ontvangt hier een e-mail over.
        </p>
        <label className="block text-sm font-semibold text-fg-soft mb-2">
          Reden (optioneel)
        </label>
        <textarea
          value={rejectModal.reason}
          onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
          rows={3}
          placeholder="Bijv. ontbrekende informatie, dubbele vermelding, ongepaste inhoud..."
          className="sf-input resize-y"
          disabled={rejectModal.submitting}
        />
        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            onClick={closeRejectModal}
            disabled={rejectModal.submitting}
          >
            Annuleren
          </Button>
          <Button
            variant="danger"
            onClick={confirmReject}
            loading={rejectModal.submitting}
          >
            Depubliceren
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PendingEventsPanel;
